import React, { useState, useEffect } from "react";

import { useTrackApiCalls } from "../../../utilities/useTrackApiCalls";

import { searchAttendees } from "../../../utilities/api";

import Form from "react-bootstrap/Form";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Stack from "react-bootstrap/Stack";
import Card from "react-bootstrap/Card";

import { SearchBar } from "../../SearchBar";
import { ExportBtn } from "../../ExportBtn";
import { Attendee } from "./Attendee";
import { EmptyContainer } from "../../EmptyContainer";
import { LoadingContainer } from "../../LoadingContainer";

export default function AttendeeList({
  eventTimezone,
  attendees,
  eventId,
  checkInAttendee,
  hasEventEnded,
}) {
  const { incrementCallId, isLastCall } = useTrackApiCalls();

  const statusOpt = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Check in",
      value: "checkIn",
    },
    {
      label: "Checked in",
      value: "checkedIn",
    },
  ];

  const [isLoading, setIsLoading] = useState(false);

  // attendees with filters applied
  const [filteredAttendees, setFilteredAttendees] = useState(attendees);

  const [query, setQuery] = useState("");

  const [status, setStatus] = useState(statusOpt[0].value);

  useEffect(() => {
    // don't call api endpoint again if nothing has changed (on initial render)
    if (
      !query &&
      status === "all" &&
      filteredAttendees?.length === attendees?.length
    )
      return;

    // if not already true
    if (!isLoading) setIsLoading(true);

    const callId = incrementCallId(); // Increment for each new call

    // Timeout so we only send last one when typing
    const debounceSearch = setTimeout(() => {
      getFilteredAttendees(callId);
    }, 1000); // 1 second

    // Cleanup function to clear the timeout if the effect runs again before the delay is over
    return () => clearTimeout(debounceSearch);
  }, [attendees, query, status, eventId]);

  const getFilteredAttendees = (callId) => {
    searchAttendees(eventId, query)
      .then((res) => {
        // if on last api call set state
        if (isLastCall(callId)) {
          if (status === "all") setFilteredAttendees(res.data);
          if (status === "checkIn")
            setFilteredAttendees(res.data?.filter((d) => !d.checkedIn));
          if (status === "checkedIn")
            setFilteredAttendees(res.data?.filter((d) => d.checkedIn === true));
          setIsLoading(false);
        }
      })
      .catch((err) => {
        // if on last api call set state
        if (isLastCall(callId)) {
          console.error(err);
          setIsLoading(false);
        }
      });
  };

  // sort by alphabetical order
  const sortBy = (arr) => {
    return arr.sort((a, b) =>
      a?.users_permissions_user?.firstName
        .toLowerCase()
        .localeCompare(b?.users_permissions_user?.firstName.toLowerCase()),
    );
  };

  const getText = () => {
    const obj = statusOpt.find((opt) => opt.value === status);

    if (obj.value == "all") {
      return;
    } else {
      return obj.label.toLowerCase();
    }
  };

  const handleClick = (a) => {
    checkInAttendee(a);
  };

  return (
    <>
      <header className="section-header">
        <div className="actions-group-flex">
          <FloatingLabel controlId="list-status" label="List status">
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {statusOpt.map((status, index) => (
                <option key={index} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
          <SearchBar
            getQuery={setQuery}
            placeholder="Search by name, phone number or email"
          />
          <ExportBtn
            data={filteredAttendees}
            timezone={eventTimezone}
            exportedData="attendees"
          />
        </div>
      </header>
      {isLoading ? (
        <LoadingContainer />
      ) : (
        <>
          {filteredAttendees.length > 0 ? (
            <Stack as="ul" gap={3}>
              {sortBy(filteredAttendees).map((attendee) => (
                <Attendee
                  key={attendee.id}
                  timezone={eventTimezone}
                  attendee={attendee}
                  handleClick={handleClick}
                  hasEventEnded={hasEventEnded}
                />
              ))}
            </Stack>
          ) : (
            <>
              <Card body>
                <EmptyContainer style="center lg">
                  <p>
                    No {getText()} attendees found
                    {query && ` matching '${query}'`}. Please try again.
                  </p>
                </EmptyContainer>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
